import { useState } from 'react'
import { Link, Outlet, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Building2, CircleUserRound, House, LogOut, Mail, Settings, Users, type LucideIcon } from 'lucide-react'
import { releaseDevice } from '@/features/push/device'
import { forgetPushSync, usePushSync } from '@/features/push/usePushDevice'
import { partnerApi } from '@/lib/api'
import { meQuery } from '@/lib/queries'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Wordmark } from './Wordmark'

/* Spec PARTNER-001 — moldura da area autenticada.
   Celular: barra superior (marca + conta) e barra inferior (Inicio, Criancas, Mensagens).
   Telas largas (>= lg): menu lateral fixo com os mesmos destinos e a conta no rodape. */

type NavPath = '/inicio' | '/criancas' | '/mensagens'
const NAV: { to: NavPath; labelKey: string; Icon: LucideIcon }[] = [
    { to: '/inicio', labelKey: 'shell.nav.home', Icon: House },
    { to: '/criancas', labelKey: 'shell.nav.children', Icon: Users },
    { to: '/mensagens', labelKey: 'shell.nav.messages', Icon: Mail },
]

function useLogout() {
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    return useMutation({
        // PARTNER-002 D-11: desliga os avisos deste aparelho antes de encerrar a sessao (aparelho compartilhado)
        mutationFn: async () => {
            await releaseDevice()
            forgetPushSync()
            await partnerApi.logout()
        },
        // Sai mesmo se a chamada falhar (sessao ja expirada): limpa o cache e volta para a entrada.
        onSettled: () => {
            queryClient.clear()
            void navigate({ to: '/entrar', replace: true })
        },
    })
}

function AccountSummary() {
    const { t } = useTranslation()
    const me = useQuery(meQuery)
    if (!me.data) return null
    const { user, organization } = me.data
    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-0.5">
                <span className="font-bold break-words">{user.name}</span>
                <span className="text-[0.82rem] break-all text-ink-muted">{user.email}</span>
                <span className="text-[0.78rem] font-semibold text-ink-muted">{t(`shell.account.role_${user.role}`)}</span>
            </div>
            <div className="flex items-start gap-2.5 rounded-2xl bg-subtle p-3">
                <Building2 className="mt-0.5 size-[18px] shrink-0 text-brand-strong" aria-hidden="true" />
                <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="label-caps text-ink-muted">{t('shell.account.organization')}</span>
                    <span className="text-[0.88rem] font-semibold break-words">{organization.name}</span>
                    <span className="text-[0.78rem] text-ink-muted">
                        {t('home.code', { code: organization.code })}
                        {organization.city ? ` · ${organization.city}${organization.state ? `/${organization.state}` : ''}` : ''}
                    </span>
                </div>
            </div>
        </div>
    )
}

/** PARTNER-004 D-01: o menu da conta leva para Configuracoes (idioma das mensagens, avisos e seguranca ficam la). */
function SettingsLink({ onNavigate }: { onNavigate?: () => void }) {
    const { t } = useTranslation()
    return (
        <Link
            to="/configuracoes"
            onClick={onNavigate}
            className="flex min-h-11 items-center gap-2 rounded-xl px-1 text-[0.9rem] font-semibold text-brand-strong no-underline hover:underline data-[status=active]:underline"
        >
            <Settings className="size-[18px]" aria-hidden="true" />
            {t('shell.account.settings')}
        </Link>
    )
}

function LogoutButton({ block = false }: { block?: boolean }) {
    const { t } = useTranslation()
    const logout = useLogout()
    return (
        <Button variant="outlineDanger" size="sm" block={block} loading={logout.isPending} onClick={() => logout.mutate()}>
            {logout.isPending ? null : <LogOut className="size-[18px]" aria-hidden="true" />}
            {logout.isPending ? t('shell.account.loggingOut') : t('shell.account.logout')}
        </Button>
    )
}

function SideNav() {
    const { t } = useTranslation()
    return (
        <aside className="fixed inset-y-0 left-0 z-20 hidden w-64 flex-col gap-6 border-r border-line bg-surface px-4 py-6 lg:flex">
            <Link to="/inicio" className="flex min-h-11 items-center px-2 no-underline">
                <Wordmark />
            </Link>
            <nav aria-label={t('shell.nav.label')}>
                <ul className="m-0 flex list-none flex-col gap-1 p-0">
                    {NAV.map(({ to, labelKey, Icon }) => (
                        <li key={to}>
                            <Link
                                to={to}
                                className="flex min-h-11 items-center gap-3 rounded-xl px-3 text-[0.92rem] font-semibold text-ink-muted no-underline hover:bg-ink/5 hover:text-ink data-[status=active]:bg-brand-soft data-[status=active]:font-bold data-[status=active]:text-brand-strong"
                            >
                                <Icon className="size-5 shrink-0" aria-hidden="true" />
                                {t(labelKey)}
                            </Link>
                        </li>
                    ))}
                </ul>
            </nav>
            <section aria-label={t('shell.account.title')} className="mt-auto flex flex-col gap-3 border-t border-line pt-4 text-[0.9rem]">
                <AccountSummary />
                <SettingsLink />
                <LogoutButton block />
            </section>
        </aside>
    )
}

function TopBar() {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    return (
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-line bg-surface px-4 pt-safe-top box-content lg:hidden">
            <Link to="/inicio" className="flex min-h-11 items-center no-underline">
                <Wordmark />
            </Link>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={t('shell.account.open')}
                aria-haspopup="dialog"
                className="flex size-11 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-ink hover:bg-ink/5"
            >
                <CircleUserRound className="size-7" strokeWidth={1.8} aria-hidden="true" />
            </button>
            <Dialog open={open} onClose={() => setOpen(false)} title={t('shell.account.title')}>
                <AccountSummary />
                <SettingsLink onNavigate={() => setOpen(false)} />
                <LogoutButton block />
            </Dialog>
        </header>
    )
}

function BottomNav() {
    const { t } = useTranslation()
    return (
        <nav aria-label={t('shell.nav.label')} className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-surface pb-safe-bottom lg:hidden">
            <ul className="mx-auto m-0 grid max-w-lg list-none grid-cols-3 p-0 px-1.5 py-1.5">
                {NAV.map(({ to, labelKey, Icon }) => (
                    <li key={to}>
                        <Link
                            to={to}
                            className="flex min-h-[3.4rem] flex-col items-center justify-center gap-1 rounded-xl text-[0.7rem] font-semibold text-ink-muted no-underline data-[status=active]:font-bold data-[status=active]:text-brand"
                        >
                            {({ isActive }) => (
                                <>
                                    <Icon className="size-6" strokeWidth={isActive ? 2.3 : 1.9} aria-hidden="true" />
                                    <span>{t(labelKey)}</span>
                                </>
                            )}
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    )
}

export function AppLayout() {
    usePushSync()
    return (
        <div className="min-h-dvh bg-app-background">
            <SideNav />
            <TopBar />
            <div className="lg:pl-64">
                <main className="mx-auto flex w-full max-w-lg flex-col gap-5 px-5 pt-5 pb-[calc(6rem+var(--spacing-safe-bottom))] lg:max-w-4xl lg:px-8 lg:pt-10 lg:pb-12">
                    <Outlet />
                </main>
            </div>
            <BottomNav />
        </div>
    )
}
